import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyableText } from '~/components/common/copyable-text';
import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { cn } from '~/utils/cn';
import { formatDateTime, formatRelative } from '~/utils/date';

import type { StageErrorRootCause } from '../../types/stage-errors.api.types';

interface StageErrorMetaProps {
  affectedCount?: number | null | undefined;
  occurrences?: number | null | undefined;
  lastOccurredAt?: string | null | undefined;
}

/**
 * Inline meta after a stage-error headline (contract §5.3): "· N products",
 * "×N" when recorded more than once, and how long ago it last happened.
 */
export function StageErrorMeta({
  affectedCount,
  occurrences,
  lastOccurredAt,
}: StageErrorMetaProps) {
  const { t } = useTranslation('catalog-operations');
  const parts: string[] = [];
  if (affectedCount != null) parts.push(t('stageErrors.affected', { count: affectedCount }));
  if (occurrences != null && occurrences > 1) {
    parts.push(t('stageErrors.occurrences', { count: occurrences }));
  }
  if (lastOccurredAt) parts.push(formatRelative(lastOccurredAt));
  if (parts.length === 0) return null;
  return <span className="text-muted-foreground text-xs">{parts.join(' · ')}</span>;
}

interface StageErrorDetailsProps {
  rootCause: StageErrorRootCause | null | undefined;
  lastOccurredAt?: string | null | undefined;
  /** Run attempt that recorded it — support/debug only. */
  runId?: string | null | undefined;
  className?: string;
  /** Root `data-testid`; the toggle gets `${testId}-toggle`. */
  testId?: string;
}

/**
 * "Details" expander for a stage error's technical cause (§5, §3.3): type,
 * raw message (monospace, scrollable), reporting service and a copyable run
 * ref. The raw text only ever appears here — never as a headline or toast.
 * Renders nothing when no root cause was recorded.
 */
export function StageErrorDetails({
  rootCause,
  lastOccurredAt,
  runId,
  className,
  testId = 'batch-funnel-stage-error-details',
}: StageErrorDetailsProps) {
  const { t } = useTranslation('catalog-operations');
  const [open, setOpen] = useState(false);
  if (!rootCause || (!rootCause.type && !rootCause.message && !rootCause.ref)) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className} data-testid={testId}>
      <CollapsibleTrigger asChild>
        <Button
          data-testid={`${testId}-toggle`}
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground h-auto cursor-pointer gap-1 px-0 py-0.5 text-xs"
        >
          <ChevronRight
            className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
            aria-hidden="true"
          />
          {t('stageErrors.details')}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <dl className="bg-background mt-1 space-y-2 rounded-lg border px-3 py-2 text-xs">
          {rootCause.type && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.type')}</dt>
              <dd className="text-foreground font-mono break-all">{rootCause.type}</dd>
            </div>
          )}
          {rootCause.message && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.message')}</dt>
              <dd className="bg-muted text-foreground mt-0.5 max-h-48 overflow-auto rounded px-2 py-1 font-mono break-all whitespace-pre-wrap">
                {rootCause.message}
              </dd>
            </div>
          )}
          {rootCause.service && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.service')}</dt>
              <dd className="text-foreground">{rootCause.service}</dd>
            </div>
          )}
          {rootCause.ref && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.ref')}</dt>
              <dd>
                <CopyableText
                  text={rootCause.ref}
                  className="text-foreground font-mono break-all"
                  copyMessage={t('stageErrors.copied')}
                  ariaLabel={t('stageErrors.copyRef')}
                  testId={`${testId}-ref`}
                />
              </dd>
            </div>
          )}
          {runId && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.runId')}</dt>
              <dd>
                <CopyableText
                  text={runId}
                  className="text-foreground font-mono break-all"
                  copyMessage={t('stageErrors.copied')}
                  ariaLabel={t('stageErrors.copyRunId')}
                  testId={`${testId}-run-id`}
                />
              </dd>
            </div>
          )}
          {lastOccurredAt && (
            <div>
              <dt className="text-muted-foreground">{t('stageErrors.rootCause.lastSeen')}</dt>
              <dd className="text-foreground">{formatDateTime(lastOccurredAt)}</dd>
            </div>
          )}
        </dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
